import type {
    LinearInterpolator,
    TRANSITION_EVENTS,
    WebMercatorViewport,
} from "@deck.gl/core";
import type { Point } from "@deck.gl-community/editable-layers";
import type { FeatureCollection, Polygon } from "geojson";
import { atom } from "jotai";
import { atomFamily, atomWithReset, atomWithStorage } from "jotai/utils";
import {
    type AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    type DataSurveyTypeEnum,
    EventStatusEnum,
    InfrastructureEquipmentListInfraTypeEnum,
    PipelineProductEnum,
    PipelineTypeEnum,
} from "../../apiClient/generated";
import { emptyFeatureCollection } from "../../utils/geopatialUtils";
import type { BasemapList } from "./basemaps";
import type { HeatmapColorRampId } from "./constants";

type MapViewState = {
    longitude: number;
    latitude: number;
    zoom: number;
    bearing: number;
    pitch: number;
    transitionInterpolator?: LinearInterpolator;
    transitionDuration?: number;
    transitionInterruption?: (typeof TRANSITION_EVENTS)[keyof typeof TRANSITION_EVENTS];
    onTransitionEnd?: () => void;
};

export interface MapState {
    _viewState: MapViewState;
    debounced: {
        viewState?: MapViewState;
        areaOnScreen?: Polygon;
        viewport?: WebMercatorViewport;
    };
    basemap: BasemapList | null;
}

interface MapStateFamily {
    mapId: string;
    initialState?: Partial<MapState>;
}

// Generic atom that allows us to generate custom map
// states using the same basic structure.
export const mapStateFamily = atomFamily(
    ({ initialState }: MapStateFamily) =>
        atomWithReset<MapState>({
            _viewState: {
                longitude: -98.58,
                latitude: 39.82,
                zoom: 4,
                bearing: 0,
                pitch: 0,
            },
            debounced: {},
            basemap: null,
            ...initialState,
        }),
    (a, b) => a.mapId === b.mapId,
);

/**
 * Map Data State
 *
 * Used for things that are strictly done in the frontend.
 * Examples: sidebar state, plume opacity states, etc.
 *
 */
export type MapSelectedContext = {
    siteId?: string;
    pipeline?: {
        id: string;
        coordinates: number[];
    };
    emissionRecordId?: string;
    dataPointId?: string;
    unmatchedDataPointId?: string;
    // Related plume: controls behavior of plumes showing on map.
    // * String: plume ID to display
    // * null: hide all plumes
    // * undefined: show all plumes
    relatedPlume?: string | null;
    // Marker
    selectedLocation?: Point;
    // Date range of visible points
    dateRangeToShowData?: {
        start?: string;
        end?: string;
    };
    // Selected extra data layer
    extraDataId?: string;
    // Multi-selection of sites, used to filter layers on map only
    // WARN: these two bypass other filters!
    // undefined -> bypass filter
    // [] -> show nothing (!)
    // [id1, id2] -> only shows ids
    emissionRecordIds?: string[];
    plumeIds?: string[];
};

export interface MapDataState {
    selectedContext: MapSelectedContext;
    mapSettings: {
        plumeOpacity: number;
        showPlumes: boolean;
    };
}

interface MapDataStateFamily {
    mapId: string;
    initialState?: Partial<MapDataState>;
}

export const mapDataStateFamily = atomFamily(
    ({ initialState }: MapDataStateFamily) =>
        atomWithReset<MapDataState>({
            selectedContext: {},
            mapSettings: {
                plumeOpacity: 0.5,
                showPlumes: true,
            },
            ...initialState,
        }),
    (a, b) => a.mapId === b.mapId,
);

export type FilterEmissionsBy = "detection_date" | "match_date";

/**
 * MapFiltersState
 *
 * Used strictly for keeping state related to data-fetching
 * mechanisms and filters that can be applied on the backend
 * directly.
 */
export interface MapFiltersState {
    infrastructure: {
        showInfrastructure: boolean;
        infraTypeFilter: InfrastructureEquipmentListInfraTypeEnum[];
        pipelineProduct: PipelineProductEnum[];
        pipelineType: PipelineTypeEnum[];
    };
    emissions: {
        showEmissions: boolean;
        emissionGroupFiltersV2: {
            thirdParty:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
            thirdPartyPublic:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
            selfReported:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
            epa:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
            unmatchedDataPoints:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
        };
        filterEmissionsBy: FilterEmissionsBy;
        // Store dates as ISO formatted dates to avoid serialization issues.
        startDateFilter: string;
        endDateFilter: string;
        // Detected rate ranges
        detectedRateMin: number | null;
        detectedRateMax: number | null;
        // Event statuses
        eventStatus: EventStatusEnum[];
    };
    nonDetects: {
        filters: {
            providers:
                | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                | "all";
        };
    };
    analytics: {
        emissionHeatmaps: {
            enabled: boolean;
            sourceData:
                | "percentage_of_obs"
                | "count_emissions"
                | "count_non_detects";
            colorMap: HeatmapColorRampId;
            opacity: number;
            filters: {
                detectionThreshold: number;
                providerWithSource:
                    | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
                    | "all";
            };
        };
    };
    extraData: {
        [key in DataSurveyTypeEnum]: boolean;
    };
}

export const defaultMapFilterState: MapFiltersState = {
    infrastructure: {
        showInfrastructure: true,
        infraTypeFilter: [
            InfrastructureEquipmentListInfraTypeEnum.Site,
            InfrastructureEquipmentListInfraTypeEnum.Equipment,
        ],
        pipelineProduct: [
            PipelineProductEnum.NaturalGas,
            PipelineProductEnum.Ngl,
        ],
        pipelineType: [
            PipelineTypeEnum.Distribution,
            PipelineTypeEnum.Gathering,
            PipelineTypeEnum.Transmission,
        ],
    },
    emissions: {
        showEmissions: true,
        emissionGroupFiltersV2: {
            thirdParty: "all",
            thirdPartyPublic: [],
            selfReported: "all",
            epa: [],
            unmatchedDataPoints: [],
        },
        filterEmissionsBy: "detection_date",
        startDateFilter: new Date(
            new Date().setMonth(new Date().getMonth() - 36),
        ).toISOString(),
        endDateFilter: new Date().toISOString(),
        detectedRateMin: null,
        detectedRateMax: null,
        eventStatus: Object.values(EventStatusEnum).filter(
            (status) => status !== EventStatusEnum.Rejected,
        ),
    },
    nonDetects: {
        filters: {
            providers: "all",
        },
    },
    analytics: {
        emissionHeatmaps: {
            enabled: false,
            sourceData: "percentage_of_obs",
            colorMap: "heat",
            opacity: 100,
            filters: {
                detectionThreshold: 100000,
                providerWithSource: "all",
            },
        },
    },
    extraData: {
        CHAMPION_X: false,
    },
};

interface MapFiltersStateFamily {
    mapId: string;
    initialState?: Partial<MapFiltersState>;
}

export const mapFiltersStateFamily = atomFamily(
    ({ initialState }: MapFiltersStateFamily) =>
        atomWithStorage<MapFiltersState>(
            "mapFilterState",
            {
                ...defaultMapFilterState,
                ...initialState,
            },
            {
                getItem: (key, initialState) => {
                    if (initialState) return initialState;

                    const value = localStorage.getItem(key);
                    if (!value) return defaultMapFilterState;

                    const restored = JSON.parse(value);
                    return {
                        emissions: {
                            ...defaultMapFilterState.emissions,
                            ...restored.emissions,
                            emissionGroupFiltersV2: {
                                ...defaultMapFilterState.emissions
                                    .emissionGroupFiltersV2,
                                ...restored.emissions?.emissionGroupFiltersV2,
                            },
                        },
                        infrastructure: {
                            ...defaultMapFilterState.infrastructure,
                            ...restored.infrastructure,
                        },
                        analytics: {
                            emissionHeatmaps: {
                                ...defaultMapFilterState.analytics
                                    .emissionHeatmaps,
                                ...restored.analytics?.emissionHeatmaps,
                            },
                        },
                        nonDetects: {
                            ...defaultMapFilterState.nonDetects,
                            ...restored.nonDetects,
                            filters: {
                                ...defaultMapFilterState.nonDetects.filters,
                                ...restored.nonDetects?.filters,
                            },
                        },
                        extraData: {
                            ...defaultMapFilterState.extraData,
                            ...restored.extraData,
                        },
                    };
                },
                setItem: (key, value) => {
                    localStorage.setItem(key, JSON.stringify(value));
                },
                removeItem: localStorage.removeItem,
            },
            {
                getOnInit: !initialState,
            },
        ),
    (a, b) => a.mapId === b.mapId,
);

/**
 * MapAreaFilters
 *
 * Used to track area filtering in the maps.
 */
export interface MapAreaFiltersState {
    selectedFilters: number[];
    drawnShape: FeatureCollection;
    drawingMode: "DRAW" | "EDIT";
    filterData: boolean;
    isDrawing: boolean;
}

const defaultMapAreaFiltersState: MapAreaFiltersState = {
    isDrawing: false,
    drawingMode: "DRAW",
    filterData: false,
    selectedFilters: [],
    drawnShape: emptyFeatureCollection,
};

interface MapAreaFiltersStateFamily {
    mapId: string;
    initialState?: Partial<MapAreaFiltersState>;
}

export const mapMapAreaFiltersStateFamily = atomFamily(
    ({ initialState }: MapAreaFiltersStateFamily) =>
        atomWithStorage<MapAreaFiltersState>("mapAreaFilterState", {
            ...defaultMapAreaFiltersState,
            ...initialState,
        }),
    (a, b) => a.mapId === b.mapId,
);

export const automaticPanAndZoomAtom = atomWithStorage(
    "automaticPanAndZoom",
    true,
);
export const prevMapViewStateAtom = atom<MapViewState>();
